// Which URL parameters make a visit a campaign visit (shared contract C1/C2, the same rules as the Worker's).
//
// Small and dependency-free on purpose: the marketing site's startup imports it through handoff/appHandoff.ts
// and the app's startup through AppRouter. The touches themselves (building, storing, the user doc field, the
// OpenPanel properties) live in acquisition-attribution.ts, which the app downloads after startup
// (attribution-module-loaders.ts). scripts/checkStartupBudget.mjs measures both startup graphs.

export type AcquisitionClickIdType = "fbclid" | "gclid" | "ttclid";

export const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_id"] as const;

// `utm_source` values that mean Meta (Facebook, Instagram, Messenger, Audience Network).
export const META_UTM_SOURCES: ReadonlySet<string> = new Set([
  "facebook",
  "fb",
  "instagram",
  "ig",
  "meta",
  "msg",
  "an",
]);

const META_AD_ID_PARAMS = ["fb_ad_id", "fb_adset_id"] as const;
const CLICK_ID_PARAMS: ReadonlyArray<AcquisitionClickIdType> = ["fbclid", "gclid", "ttclid"];

/**
 * URL parameters that describe one campaign visit. They are remembered and forwarded as one set: a new
 * campaign visit replaces the whole set, so values from two campaigns never mix. The TikTok campaign id and
 * click id are part of it, so an earlier TikTok click never rides along with a later Meta or Google campaign
 * (the landing page reports a TikTok conversion whenever it forwards `ttc_id`).
 */
export const CAMPAIGN_ATTRIBUTION_PARAMS: ReadonlyArray<string> = [
  ...UTM_PARAMS,
  "fb_adset_id",
  "fb_ad_id",
  "fb_placement",
  "fb_site",
  "gclid",
  "fbclid",
  "ttclid",
  "ttc_id",
];

export function isMetaUtmSource(utmSource: string | null | undefined): boolean {
  return Boolean(utmSource) && META_UTM_SOURCES.has(utmSource!.trim().toLowerCase());
}

/**
 * Contract C1: a visit counts as "from a Meta ad" when `fb_ad_id` is present, `utm_source` is a Meta
 * source, or `fbclid` is present (Meta also adds fbclid to organic links, so this is deliberately broad;
 * the ad ids distinguish paid traffic).
 */
export function isMetaAdVisit(params: URLSearchParams): boolean {
  return Boolean(params.get("fb_ad_id")) || isMetaUtmSource(params.get("utm_source")) || Boolean(params.get("fbclid"));
}

/** Push notification and email links are re-engagement, not acquisition. */
export function isExcludedUtmSource(utmSource: string | null): boolean {
  if (!utmSource) return false;
  const normalizedUtmSource = utmSource.trim().toLowerCase();
  return normalizedUtmSource.includes("push_notification") || normalizedUtmSource === "email";
}

/** Whether the URL carries any campaign parameter at all (used to forward/remember them as one set). */
export function hasCampaignAttributionParams(params: URLSearchParams): boolean {
  return CAMPAIGN_ATTRIBUTION_PARAMS.some((param) => Boolean(params.get(param)));
}

/**
 * Contract C2: a visit is a touch only if its URL has a utm_* parameter, a Meta ad id or a click id, and its
 * utm_source is not a push notification or email (those are re-engagement, not acquisition).
 */
export function isAcquisitionTouchVisit(params: URLSearchParams): boolean {
  if (isExcludedUtmSource(params.get("utm_source"))) return false;
  return (
    UTM_PARAMS.some((param) => Boolean(params.get(param))) ||
    META_AD_ID_PARAMS.some((param) => Boolean(params.get(param))) ||
    CLICK_ID_PARAMS.some((param) => Boolean(params.get(param)))
  );
}
